import React, { Fragment } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { Divider } from 'components/shared/Divider';
import { formatCurrency } from 'helpers/formatters';

type MarketplaceListingHistory = MarketplaceProperty['listingHistory'][number];

interface PriceHistoryProps {
  listingHistory: MarketplaceProperty['listingHistory'];
}

export const PriceHistory: React.FC<PriceHistoryProps> = ({ listingHistory }) => {
  // Dedupe by date after cleaning up timestamp format
  const listingHistoryByDateMap = listingHistory.reduce<Record<string, MarketplaceListingHistory>>(
    (map, history) => {
      if (!history.timestamp) {
        return map;
      }

      const date = new Date(history.timestamp).toLocaleDateString('en-us', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (!map[date]) {
        map[date] = { ...history, timestamp: date };
      }

      return map;
    },
    {},
  );

  // Non-MLS pending events do not represent a public price change.
  const pricingEvents = Object.values(listingHistoryByDateMap)
    .filter((history) => history.state !== 'SALE_PENDING' || history.mls)
    .filter(
      (history) =>
        history.soldPriceCents || history.currentListPriceCents || history.originalListPriceCents,
    );
  const rows = pricingEvents.map((history, index) => {
    const state = history.state;
    const priceCents =
      state === 'SOLD' || state === 'OFF_MARKET'
        ? history.soldPriceCents || history.currentListPriceCents || 0
        : history.currentListPriceCents || history.originalListPriceCents || 0;

    return (
      <Fragment key={history.timestamp}>
        <Row
          className={cn(
            'items-center justify-between',
            index === pricingEvents.length - 1 && 'pb-6',
          )}
        >
          <Col>
            <Text className="text-body-sm text-od-content-primary">{history.timestamp}</Text>
            <Text as="span" className="text-body-sm capitalize text-od-content-secondary">
              {state.split('_').join(' ').toLowerCase()}
              {history.mls && ` · ${history.mls}`}
            </Text>
          </Col>
          <Text as="strong" className="text-label-sm text-od-content-primary">
            {formatCurrency(priceCents)}
          </Text>
        </Row>
        {index !== pricingEvents.length - 1 && <Divider className="py-4" />}
      </Fragment>
    );
  });

  const description = rows.length ? rows : 'No price history';

  return (
    <Collapsible.Root defaultOpen className="w-full">
      <Collapsible.Trigger className="group flex w-full items-center justify-between gap-2 py-6">
        <Text
          as="span"
          className="text-left text-label-xl text-od-content-primary lg:text-subheader-xs"
        >
          Price history
        </Text>
        <Box className="shrink-0 text-od-content-primary transition-transform duration-200 group-data-[panel-open]:rotate-180">
          <IconChevronDown size={20} />
        </Box>
      </Collapsible.Trigger>
      <Collapsible.Panel className="pb-4">
        {typeof description === 'string' ? (
          <Text className="text-body-md text-od-content-primary">{description}</Text>
        ) : (
          description
        )}
      </Collapsible.Panel>
    </Collapsible.Root>
  );
};
